const DISBURSEMENT_GRAPH_KEYS = new Set([
  "disbursement",
  "disbWithAdvance",
  "accumulatedDisbursement",
  "advancementTo",
  "advancementFrom",
]);

const unique = (values) => [...new Set(values)];

const selectedSource = (policy) =>
  policy?.effectiveSource ?? policy?.requestedSource ?? null;

/**
 * Apply the organization's planning policy to one configured chart.
 *
 * `ensurePlanningSeries` decides whether the chosen planning curve may be added when the
 * user's configuration omits it. Only the projection chart wants that; the advancements
 * and balance charts must stay subtractive, otherwise the policy injects a cash-flow
 * curve into a chart that is not about cash-flow planning at all.
 */
export const filterCashFlowGraphSeries = (
  configured = [],
  policy,
  { ensurePlanningSeries = true } = {},
) => {
  const values = Array.isArray(configured) ? configured : [];
  const source = selectedSource(policy);
  const ensure = (keys) => (ensurePlanningSeries ? keys : []);
  if (source === "physical_financial") {
    return unique([
      ...ensure(["physicals"]),
      ...values.filter(
        (key) => !DISBURSEMENT_GRAPH_KEYS.has(key) && key !== "physicals",
      ),
    ]);
  }
  if (source === "disbursement") {
    return unique([
      ...ensure(["disbursement"]),
      ...values.filter((key) => key !== "physicals" && key !== "disbursement"),
    ]);
  }
  // Nothing competes in the combined view, so no series is removed: the disbursement
  // chart keeps its accumulated curve and the advancements chart keeps the series it is
  // built from. Only the ordering is fixed so the two planning curves come first.
  if (source === "combined") {
    return unique([...ensure(["physicals", "disbursement"]), ...values]);
  }
  return [...values];
};

export const filterCashFlowColumns = (configured = [], policy) => {
  const values = Array.isArray(configured) ? configured : [];
  const source = selectedSource(policy);
  if (source === "physical_financial") {
    return unique([
      "physical",
      ...values.filter((key) => key !== "physical" && key !== "disbursement"),
    ]);
  }
  if (source === "disbursement") {
    return unique([
      "disbursement",
      ...values.filter((key) => key !== "physical" && key !== "disbursement"),
    ]);
  }
  if (source === "combined") {
    return unique([
      "physical",
      "disbursement",
      ...values.filter((key) => key !== "physical" && key !== "disbursement"),
    ]);
  }
  return [...values];
};

export const filterCashFlowExport = (
  { fields = [], resources = [] } = {},
  policy,
) => {
  const source = selectedSource(policy);
  const filteredFields = (Array.isArray(fields) ? fields : [])
    .filter((field) => {
      if (source === "physical_financial") {
        return !DISBURSEMENT_GRAPH_KEYS.has(field.key);
      }
      if (source === "disbursement") return field.key !== "physicals";
      return true;
    })
    .map((field) => {
      if (["physical_financial", "combined"].includes(source) && field.key === "physicals") {
        return {
          ...field,
          label: source === "combined"
            ? physicalFinancialLabel(policy)
            : policy?.physicalFinancial?.source
              ? physicalFinancialLabel(policy)
              : "Previsão de caixa (Físico-financeiro)",
        };
      }
      if (["disbursement", "combined"].includes(source) && field.key === "disbursement") {
        return {
          ...field,
          label:
            policy?.state === "needs_adjustment"
              ? "Desembolso (em ajuste)"
              : "Previsão de caixa (Desembolso)",
        };
      }
      return { ...field };
    });
  const keys = new Set(filteredFields.map((field) => field.key));
  const filteredResources = (Array.isArray(resources) ? resources : []).map(
    (row) =>
      Object.fromEntries(Object.entries(row).filter(([key]) => keys.has(key))),
  );
  return { fields: filteredFields, resources: filteredResources };
};

export const isCashFlowRawExportVisible = (exportType, policy) => {
  const source = selectedSource(policy);
  if (source === "physical_financial") {
    return !["disbursements", "advancements"].includes(exportType);
  }
  if (source === "disbursement") return exportType !== "physical_financials";
  if (source === "combined") return true;
  return true;
};

/**
 * Whether the disbursement journey is read-only under the organization's policy.
 *
 * Only the physical-financial view retires the disbursement schedule; the combined view
 * keeps advancements editable, because its whole point is that both curves stay live.
 * Read `requestedSource`, not `effectiveSource`: the policy is what the organization
 * decided, while the effective source also reflects how complete the coverage happens
 * to be right now.
 */
export const isDisbursementPlanningReadOnly = (policy) => (
  policy?.requestedSource === "physical_financial"
);

export const shouldShowDisbursementCoverage = (policy) => {
  if (policy?.availableSources?.disbursement !== true) return false;
  return policy.requestedSource !== "physical_financial";
};

export const cashFlowPlanningSourceLabel = (source) =>
  source === "disbursement"
    ? "Cronograma de desembolso"
    : source === "physical_financial"
    ? "Previsto físico-financeiro"
    : source === "combined"
    ? "Visão completa"
    : "Fonte ainda não escolhida";

export const physicalFinancialLabel = (policy) => {
  const source = String(policy?.physicalFinancial?.source ?? "").toLowerCase();
  if (source === "prevision") return "Previsto (Prevision)";
  if (source === "sienge") return "Previsto (Sienge)";
  if (source === "mega") return "Previsto (MEGA)";
  return "Previsto físico-financeiro";
};
